import { useState } from 'react';
import { usePendingBackdatedHoursRequests } from '../../hooks/useBackdatedHoursRequests';
import { BackdatedHoursRequestsTable } from '../../components/domain/BackdatedHoursRequestsTable';
import { Spinner } from '../../components/common/Spinner';
import { useAuth } from '../../services/authContext';

/**
 * Phase 113 (MKS-208, D-10) — the central pending queue, the admin half of the
 * approval surface. `PendingJoinRequestsPage` is the analog, including the
 * admin-only "show all projects" scope toggle.
 */
export function PendingBackdatedRequestsPage() {
  const { isAdmin } = useAuth();
  const [showAllProjects, setShowAllProjects] = useState(false);
  const {
    data: requests,
    isLoading,
    error,
  } = usePendingBackdatedHoursRequests(isAdmin && showAllProjects ? 'all' : undefined);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        Failed to load pending backdated hours requests.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Backdated Hours Requests</h1>
        <div className="flex items-center gap-4">
          {isAdmin && (
            <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={showAllProjects}
                onChange={(e) => setShowAllProjects(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Show all projects
            </label>
          )}
          <span className="text-sm text-gray-500">
            {requests?.length || 0} pending request{requests?.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <p className="text-sm text-gray-500">
        Approving writes the entry on the requester&rsquo;s behalf, already approved. The full set
        of time-entry rules still applies — the project has to be in progress and the requester
        still assigned to it — so an approval can be refused for a reason other than the lock.
      </p>

      {!requests?.length ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
          No pending backdated hours requests.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <BackdatedHoursRequestsTable requests={requests} showProject />
        </div>
      )}
    </div>
  );
}

export default PendingBackdatedRequestsPage;
